const EMPTY = '—'

export const RELATIVE_REGRESSION_THRESHOLD = 0.1

export type RegressionDirection = 'increase' | 'decrease'

export const MAIN_SERIES_KEY = 'main'
export const PR_SERIES_KEY = 'pr'

interface RegressionCheck {
  current: number | null
  baseline?: number | null
  percent: number
  direction: RegressionDirection
}

export const relativeChangeFromBaseline = (
  current: number | null,
  baseline?: number | null
): number | null => {
  if (
    current === null ||
    baseline === null ||
    baseline === undefined ||
    baseline <= 0
  ) {
    return null
  }

  return (current - baseline) / baseline
}

export const isAtLeastPercentWorse = ({
  current,
  baseline,
  percent,
  direction,
}: RegressionCheck): boolean => {
  if (current === null || baseline === null || baseline === undefined) {
    return false
  }

  const changeFromBaseline = relativeChangeFromBaseline(current, baseline)

  if (changeFromBaseline === null) {
    return false
  }

  if (direction === 'increase') {
    return changeFromBaseline >= percent
  }

  return -changeFromBaseline >= percent
}

export const formatBytes = (bytes: number | null): string => {
  if (bytes === null) {
    return EMPTY
  }

  if (bytes < 1024) {
    return `${bytes} B`
  }

  const kilobytes = bytes / 1024

  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(1)} KB`
  }

  return `${(kilobytes / 1024).toFixed(1)} MB`
}

export const formatSignedBytes = (bytes: number | null): string => {
  if (bytes === null) {
    return EMPTY
  }

  if (bytes === 0) {
    return '0 B'
  }

  const sign = bytes > 0 ? '+' : '-'

  return `${sign}${formatBytes(Math.abs(bytes))}`
}

export const formatCompileTime = (milliseconds: number | null): string => {
  if (milliseconds === null) {
    return EMPTY
  }

  if (milliseconds < 1000) {
    return `${milliseconds} ms`
  }

  return `${(milliseconds / 1000).toFixed(1)} s`
}

export const formatSignedCoverage = (percentage: number | null): string => {
  if (percentage === null) {
    return EMPTY
  }

  if (percentage === 0) {
    return '0%'
  }

  const sign = percentage > 0 ? '+' : '-'

  return `${sign}${Math.abs(percentage).toFixed(1)}%`
}

export const formatCoverage = (percentage: number | null): string => {
  if (percentage === null) {
    return EMPTY
  }

  return `${percentage}%`
}
